import { useEffect, useRef } from 'react';
import { useWhatsAppEvents as useWhatsAppContext } from '@/context/whatsapp-events-context';

interface UseWhatsAppEventsProps {
  conversationId: string;
  channelId: string | null;
  onMessageReceived?: (message?: any) => void;
}

export const useWhatsAppEvents = ({
  conversationId,
  channelId,
  onMessageReceived,
}: UseWhatsAppEventsProps) => {
  const whatsAppContext = useWhatsAppContext();
  const subscriptionIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!channelId || !whatsAppContext || !onMessageReceived) return;

    const setupEventSubscription = async () => {
      try {
        // Check if channel is already connected
        const isConnected = whatsAppContext.isChannelConnected(channelId);

        if (!isConnected) {
          console.log(`Connecting to WhatsApp channel: ${channelId}`);
          await whatsAppContext.connectChannel(channelId);
        }

        // Subscribe to message events for this channel
        subscriptionIdRef.current = whatsAppContext.subscribeToChannel(
          channelId,
          'message',
          (message) => {
            // Handle all message events for this channel
            // The conversation matching will be handled by the message processing logic
            console.log('Received WhatsApp event for channel:', channelId);
            onMessageReceived(message);
          },
        );

        console.log(
          `Subscribed to channel ${channelId} with ID: ${subscriptionIdRef.current}`,
        );
      } catch (error) {
        console.error(
          `Failed to setup WhatsApp events for channel ${channelId}:`,
          error,
        );
      }
    };

    setupEventSubscription();

    // Cleanup function
    return () => {
      if (subscriptionIdRef.current) {
        console.log(
          `Unsubscribing from WhatsApp events: ${subscriptionIdRef.current}`,
        );
        try {
          whatsAppContext.unsubscribe(subscriptionIdRef.current);
        } catch (error) {
          console.warn('Failed to unsubscribe from WhatsApp events:', error);
        }
        subscriptionIdRef.current = null;
      }
    };
  }, [conversationId, channelId, whatsAppContext, onMessageReceived]);
};
